import api from "./api";
import { generateErrorMessage } from "../utils/message";


/*Roles*/
export const registerRole = async (roleData) => {
  try {
    // Get user from localStorage
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    
    // Use role_id from form input (required field) - convert to lowercase
    const role_id = roleData.role_id?.toLowerCase().trim() || '';
    
    // Prepare payload with required fields
    const payload = {
      role_id: role_id,
      role_name: roleData.role_name,
      role_description: roleData.role_description,
      is_vendor: roleData.is_vendor !== undefined ? roleData.is_vendor : false,
      permissions: roleData.permissions || {},
      created_by: user.username || user.email || 'system'
    };
    
    const response = await api.post("/roles", payload);
    return response.data;
  } catch (error) {
    const fallback = generateErrorMessage("register", "role");
    const msg = error.response?.data?.message || fallback;
    throw new Error(msg);
  }
};

export const fetchRolesFromAPI = async (page = 1, perPage = 10, search = "", columnFilters = {}, sort = {}) => {
  try {
    const params = {
      page,
      per_page: perPage
    };

    if (search) {
      params.search = search;
    }

    // Column filters (backend reads role_name / role_description).
    if (columnFilters.role_name) params.role_name = columnFilters.role_name;
    if (columnFilters.role_description) params.role_description = columnFilters.role_description;

    if (sort && sort.sortBy) {
      params.sort_by = sort.sortBy;
      params.sort_dir = sort.sortDir === 'desc' ? 'desc' : 'asc';
    }

    const response = await api.get("/roles", {
      params: params,
    });

    // Full body: { data: { roles: <envelope> } }
    return response.data;
  } catch (error) {
    const fallback = generateErrorMessage("get data", "role");
    const msg = error.response?.data?.message || fallback;
    throw new Error(msg);
  }
};

/* Fetch every role matching the filters, walking server pages (for Export). */
export const fetchAllRolesForExport = async ({ search = "", columnFilters = {}, sort = {} } = {}, onProgress) => {
  const PER_PAGE = 1000;
  const collected = [];
  let page = 1;
  let total = 0;
  for (;;) {
    const response = await fetchRolesFromAPI(page, PER_PAGE, search, columnFilters, sort);
    const payload = response?.data?.roles || {};
    const rows = Array.isArray(payload.data) ? payload.data : [];
    collected.push(...rows);
    total = Number(payload.total) || collected.length;
    if (onProgress) onProgress(collected.length, total);
    const lastPage = Number(payload.last_page) || 1;
    if (rows.length === 0 || page >= lastPage || collected.length >= total) break;
    page += 1;
  }
  return collected;
};

export const deleteRolesFromAPI = async (roleId) => {
  try {
    const response = await api.delete(`/roles/${roleId}`, {
      params: { role_id: roleId },
    });
    return response.data;
  } catch (error) {
    const fallback = generateErrorMessage("delete data", "role");
    const msg = error.response?.data?.message || fallback;
    throw new Error(msg);
  }
};


export const fetchRolesById = async (roleId) => {
  try {
    const response = await api.get(`/roles/${roleId}`);
    return response.data;
  } catch (error) {
    const fallback = generateErrorMessage("get data role", "role");
    const msg = error.response?.data?.message || fallback;
    throw new Error(msg);
  }
};

export const updateRole = async (id, roleData) => {
  try {
    // Get user from localStorage
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    
    // Prepare payload with required fields
    const payload = {
      role_name: roleData.role_name,
      role_description: roleData.role_description,
      is_active: roleData.is_active,
      is_vendor: roleData.is_vendor !== undefined ? roleData.is_vendor : false,
      permissions: roleData.permissions || {},
      updated_by: user.username || user.email || 'system'
    };
    
    const response = await api.put(`/roles/${id}`, payload);
    return response.data;
  } catch (error) {
    const fallback = generateErrorMessage("update", "role");
    const msg = error.response?.data?.message || fallback;
    throw new Error(msg);
  }
};

/* 📌 Upload Role Excel File */
export const uploadRoleFileToAPI = async (formData, onUploadProgress) => {
  try {
    const response = await api.post("/roles/submit-excel", formData, {
      headers: { "Content-Type": "multipart/form-data" },
      // Reports how much of the FILE has reached the server (0-100). Server-side
      // row inserting happens after this hits 100 and is not tracked here.
      onUploadProgress: onUploadProgress
        ? (event) => {
            if (event.total) {
              onUploadProgress(Math.round((event.loaded / event.total) * 100));
            }
          }
        : undefined,
    });
    return response.data;
  } catch (error) {
    const fallback = generateErrorMessage("upload role file", "role");
    const msg = error.response?.data?.message || fallback;
    throw new Error(msg);
  }
};

/* ========== NEW METHODS FOR DXHUB CONCEPT ========== */

/**
 * Get menus assigned to a role (from tb_m_role_menus)
 */
export const getMenusByRole = async (roleId) => {
  try {
    const response = await api.get(`/roles/${roleId}/menus`);
    return response.data;
  } catch (error) {
    const fallback = generateErrorMessage("get menus by role", "role");
    const msg = error.response?.data?.message || fallback;
    throw new Error(msg);
  }
};

/**
 * Get permissions assigned to a role (from tb_m_role_permissions_new)
 */
export const getPermissionsByRole = async (roleId) => {
  try {
    const response = await api.get(`/roles/${roleId}/permissions`);
    return response.data;
  } catch (error) {
    const fallback = generateErrorMessage("get permissions by role", "role");
    const msg = error.response?.data?.message || fallback;
    throw new Error(msg);
  }
};
